import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Entity } from '../types/entity';
import { getEntity, createDefaultEntity } from '../lib/supabase/entity';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase/client';

export function useEntity() {
  const [entity, setEntity] = useState<Entity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user, isLoading: isAuthLoading } = useAuth();
  const [loadAttempted, setLoadAttempted] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 2;
    const retryDelay = 1000;
    
    console.log('useEntity: Loading entity for user:', user?.id);

    async function loadEntity() {
      // If already loaded or too many attempts, don't try again
      if (!isMounted || retryCount >= maxRetries) return;
      
      try {
        setLoading(true);
        setError(null);
        
        console.log('useEntity: Attempting to load entity');

        // Ensure we have an authenticated session
        try {
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            console.error('useEntity: Session error:', sessionError);
            throw new Error('Session error: ' + sessionError.message);
          }
          
          if (!session?.user?.id) {
            console.error('useEntity: No user in session');
            throw new Error('User not authenticated');
          }
          
          console.log('useEntity: Session verified');
        } catch (sessionErr) {
          console.log('Session error:', sessionErr);
          throw sessionErr;
        }

        // Verify user object is available
        if (!user?.id) {
          console.error('useEntity: User data not available');
          throw new Error('User data not available');
        }

        // Only attempt to load or create entity if profile exists
        let data;
        try {
          console.log('useEntity: Getting entity for user:', user.id);
          data = await getEntity();
          console.log('useEntity: Entity data:', data ? 'Found' : 'Not found');
        } catch (entityErr) {
          console.error('Failed to get entity:', entityErr);
          data = null;
        }
        
        if (!data) {
          try {
            console.log('useEntity: Creating default entity');
            data = await createDefaultEntity();
            console.log('Created default entity:', data?.id);
          } catch (err) {
            console.error('Failed to create default entity, continuing with null:', err);
            data = null;
          }
        }
        
        if (isMounted) {
          setEntity(data);
          setLoadAttempted(true);
          console.log('useEntity: Entity set successfully:', data?.id);
        }
      } catch (err: any) {
        console.error('Failed to load entity:', err);
        
        if (isMounted) {
          setError(err?.message || 'Failed to load entity');
          setLoadAttempted(true);
          
          // Only redirect on authentication errors
          if (err?.message?.includes('authenticated') || err?.message?.includes('User not authenticated')) {
            console.log('useEntity: Redirecting to login due to auth error');
            navigate('/login');
          }
        }
        
        // Retry after delay if not too many attempts
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(loadEntity, retryDelay);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    // Only attempt to load entity if auth loading is complete
    if (!isAuthLoading && user) {
      console.log('useEntity: Auth loading complete, user available');
      loadEntity();
    } else if (!isAuthLoading && !user) {
      console.log('useEntity: Auth loading complete, no user');
      setLoading(false);
      setLoadAttempted(true);
    }
    
    return () => {
      isMounted = false;
    };
  }, [navigate, user, isAuthLoading]);

  return { entity, loading, error };
}